import * as angular from 'angular';
import * as _ from 'lodash';
import template from './plantWareLossDashboard.tpl.html';
import { States } from "../../shared/config/constant.module";
import { StateParams } from "../../common";
import UIUtilityServiceName, { EnumOption, UIUtilityService } from "../../services/utility/uiUtility.service";
import ShopServiceName, { ShopService } from "../../services/shops/shops.service";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import AuthDataServiceName, { AuthDataService } from "../../services/auth/authData.service";
import PlantWareLossServiceName, { PlantWareLossService } from '../../services/dashboard/plantWareLoss.service';
import { IInterval, Interval } from '../../directives/interval.factory';
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import * as moment from 'moment';
import { apsDisplayEnums } from '../../shared/dto/enums';

const requires = [
	UIUtilityServiceName,
	ShopServiceName,
	LoaderServiceName,
	PlantWareLossServiceName,
	AuthDataServiceName
];

export interface CavityCorrelation {
	cavityName: string;
	color: string;
}

export interface EquipmentCounts {
	name: string;
	percentLoss: number;
}

export interface LocationLosses {
	hotEndLoss: number;
	lehrLoss: number;
	coldEndTotal: number;
	coldEndInspection: number;
	coldEndDelta: number;
}

export interface AreaEffeciency {
	lehrInEffeciency: number;
	lehrOutEffeciency: number;
	packPercent: number;
}

export interface PlantWareLossInfo {
	shopName: string;
	equipmentCounts: EquipmentCounts[];
	backCavities: CavityCorrelation[];
	middleCavities: CavityCorrelation[];
	frontCavities: CavityCorrelation[];
	locationLosses: LocationLosses;
	areaEffeciency: AreaEffeciency;
}

export class PlantWareLossDashboardController {
	plantId: string;
	shopId: string;
	shopName: string;
	plantName: string;
	shopConfig: apsViewModels.ShopConfigurationWithShifts;
	$state: angular.ui.IStateService;
	loaderService: LoaderService;
	uiUtilityService: UIUtilityService;
	authDataService: AuthDataService;
	plantWareLossService: PlantWareLossService;
	countsInterval: apsDisplayEnums.CountDisplayInterval;
	$q: angular.IQService;
	startDate: Date;
	endDate: Date;
	plantWareLossInfo: PlantWareLossInfo[];
	countDisplayIntervalOptions: EnumOption[];
	shopService: ShopService;
	$interval: angular.IIntervalService;
	interval: IInterval = null;

	private $scope: angular.IScope;

	constructor(
		$stateParams: StateParams,
		shopService: ShopService,
		loaderService: LoaderService,
		$scope: angular.IScope,
		uiUtilityService: UIUtilityService,
		$state: angular.ui.IStateService,
		plantWareLossService: PlantWareLossService,
		authDataService: AuthDataService,
		$q: angular.IQService,
		$interval: angular.IIntervalService) {

		this.shopId = $stateParams.shopId;
		this.plantId = $stateParams.plantId;
		this.loaderService = loaderService;
		this.shopName = null;
		this.plantName = null;
		this.plantWareLossInfo = null;
		this.$state = $state;
		this.$scope = $scope;
		this.uiUtilityService = uiUtilityService;
		this.plantWareLossService = plantWareLossService;
		this.$q = $q;
		this.shopService = shopService;
		this.uiUtilityService = uiUtilityService;
		this.$interval = $interval;
		this.authDataService = authDataService;

		this.$scope.$on('$destroy', () => {
			this.destroyInterval();
		});

		this.createInterval();
		this.initPage();
	}

	destroyInterval() {
		this.interval.destroy();
		this.interval = null;
	}

	createInterval() {
		this.interval = new Interval(() => {
			this.update();
		}, this.$interval, 1000 * 60 * 5);
		this.interval.start();
	}

	update() {

		this.mapCountInterval();// call this again to reset the start/end dates

		this.loaderService.setAppLoading(true);
		this.plantWareLossService
			.getPlantWareLoss(this.plantId, this.startDate, this.endDate)
			.then((plantWareLossType) => {
				this.mapPlantWareLossType(plantWareLossType);
				this.loaderService.setAppLoading(false);
			});
	}

	initPage() {
		this.loaderService.setAppLoading(true);
		this.shopService.getShopConfiguration(this.shopId)
			.then(shop => {
				this.shopName = shop.name;
				this.plantName = shop.plantName;
				this.shopConfig = shop;
			})
			.then(() => {
				this.countDisplayIntervalOptions = this.uiUtilityService
					.getListOptionsFromEnum(apsDisplayEnums.CountDisplayInterval, null, null, o => o);
			})
			.then(() => {
				this.countsInterval = this.authDataService.getCountInterval();
				this.mapCountInterval();
			})
			.then(() => {
				this.plantWareLossService
					.getPlantWareLoss(this.plantId, this.startDate, this.endDate)
					.then((plantWareLossType) => {
						this.mapPlantWareLossType(plantWareLossType);
						this.loaderService.setAppLoading(false);
					});
			});
	}

	mapCountInterval() {
		this.authDataService.setCountInterval(this.countsInterval);
		this.endDate = moment().toDate();
		let now = moment();
		let remainderMinutes = now.minute() % 10;
		let remainderSeconds = now.second();
		let remainderMilliSeconds = now.millisecond();
		switch (this.countsInterval) {
			case apsDisplayEnums.CountDisplayInterval.CurrentHour:
				this.startDate = moment().startOf('hour').toDate();
				break;
			case apsDisplayEnums.CountDisplayInterval.PreviousHour:
				this.endDate = moment().startOf('hour').toDate();
				this.startDate = moment(this.endDate).subtract(1, 'hour').toDate();
				break;
			case apsDisplayEnums.CountDisplayInterval.CurrentShift:
				this.startDate = this.shopConfig.shiftsInLast24Hours[0].startTime;
				break;
			case apsDisplayEnums.CountDisplayInterval.LastShift:
				this.endDate = this.shopConfig.shiftsInLast24Hours[1].stopTime;
				this.startDate = this.shopConfig.shiftsInLast24Hours[1].startTime;
				break;
			case apsDisplayEnums.CountDisplayInterval.ShiftBeforeLast:
				this.endDate = this.shopConfig.shiftsInLast24Hours[2].stopTime;
				this.startDate = this.shopConfig.shiftsInLast24Hours[2].startTime;
				break;
			case apsDisplayEnums.CountDisplayInterval.RollingHour:
				this.endDate = moment(now)
					.subtract(remainderMinutes, 'minute')
					.subtract(remainderSeconds, 'second')
					.subtract(remainderMilliSeconds, 'millisecond')
					.toDate();
				this.startDate = moment(this.endDate)
					.subtract(1, 'hour')
					.toDate();
				break;
			case apsDisplayEnums.CountDisplayInterval.LastTwentyMinutes:
				this.endDate = moment(now)
					.subtract(remainderMinutes, 'minute')
					.subtract(remainderSeconds, 'second')
					.subtract(remainderMilliSeconds, 'millisecond')
					.toDate();
				this.startDate = moment(this.endDate)
					.subtract(20, 'minute')
					.toDate();
				break;
			default: // default to 24 hours
				this.endDate = moment().startOf('hour').toDate();
				this.startDate = moment(this.endDate).subtract(1, 'days').toDate();
				break;
		}
	}

	mapPlantWareLossType(plantWareLossType: apsViewModels.PlantWareLossType[]) {
		const plantWareLossInfo: PlantWareLossInfo[] = [];

		_.forEach(plantWareLossType, (plant) => {
			const shopName = plant.shopName;
			const equipmentCounts = this.getEquipmentCounts(plant.countSummaries);
			const backCavities = this.getCavityCorrelations(plant.cavityCorrelations, 'B');
			const middleCavities = this.getCavityCorrelations(plant.cavityCorrelations, 'M');
			const frontCavities = this.getCavityCorrelations(plant.cavityCorrelations, 'F');
			const locationLosses = this.getLocationLosses(plant.countSummaries);
			const areaEffeciency = this.getAreaEffeciency(plant.countSummaries);

			const shopWareLoss: PlantWareLossInfo = {
				shopName: shopName,
				equipmentCounts: equipmentCounts,
				backCavities: backCavities,
				middleCavities: middleCavities,
				frontCavities: frontCavities,
				locationLosses: locationLosses,
				areaEffeciency: areaEffeciency
			};

			plantWareLossInfo.push(shopWareLoss);
		});

		this.plantWareLossInfo = plantWareLossInfo;
	}

	getAreaEffeciency(countSummaries: apsViewModels.CountSummary[]): AreaEffeciency {
		let heSummary: apsViewModels.CountSummary = this.getHotEndSummary(countSummaries);
		let lehrSummary: apsViewModels.CountSummary = this.getLehrSummary(countSummaries);
		let lehrInEfficiency: number = (lehrSummary.inspectCount / heSummary.inspectCount * 100) || 0.0;
		let lehrOutEfficiency: number = ((lehrSummary.inspectCount - lehrSummary.rejectCount) / heSummary.inspectCount * 100) || 0.0;
		let packPercent: number = this.getPackPercent(countSummaries);
		return {
			lehrInEffeciency: lehrInEfficiency,
			lehrOutEffeciency: lehrOutEfficiency,
			packPercent: packPercent
		};
	}

	getPackPercent(countSummaries: apsViewModels.CountSummary[]): number {
		let bottlesIn = 0.0;
		let bottlesOut = 0.0;
		let countLen = countSummaries.length || 0;

		if (countLen === 0) {
			return 0.0;
		}

		let lastItem = countSummaries[countLen - 1];
		bottlesIn = countSummaries[0].inspectCount;
		bottlesOut = (lastItem.rejectCount && lastItem.rejectCount > 0.0)
			? lastItem.inspectCount - lastItem.rejectCount
			: lastItem.inspectCount;

		return (bottlesOut / bottlesIn) * 100.0;
	}

	getBottlesOut(countSummaries: apsViewModels.CountSummary[]): number {
		let bottlesOut = 0.0;
		let countLen = countSummaries.length || 0;

		if (countLen === 0) {
			return 0.0;
		}

		let lastItem = countSummaries[countLen - 1];
		bottlesOut = (lastItem.rejectCount && lastItem.rejectCount > 0.0)
			? lastItem.inspectCount - lastItem.rejectCount
			: lastItem.inspectCount;

		return bottlesOut;
	}

	getLocationLosses(countSummaries: apsViewModels.CountSummary[]): LocationLosses {
		let heItem: apsViewModels.CountSummary = this.getHotEndSummary(countSummaries);
		let lehrItem: apsViewModels.CountSummary = this.getLehrSummary(countSummaries);
		let coldEndRejectCount: number = this.getColdEndInspectionLoss(countSummaries);
		let coldEndInspectionPercent: number = (coldEndRejectCount / heItem.inspectCount * 100) || 0.0;
		let bottlesOut: number = this.getBottlesOut(countSummaries);
		let lehrOut: number = (lehrItem.inspectCount - lehrItem.rejectCount) || 0.0;
		let coldEndDelta: number = ((lehrOut - bottlesOut - coldEndRejectCount) / heItem.inspectCount * 100) || 0.0;
		let coldEndTotal: number = ((lehrOut - bottlesOut) / heItem.inspectCount * 100) || 0.0;
		let hotEndLoss: number = (heItem.inspectCount - lehrItem.inspectCount) / heItem.inspectCount * 100 || 0.0;
		return {
			hotEndLoss: hotEndLoss,
			lehrLoss: lehrItem.rejectPercent,
			coldEndTotal: coldEndTotal,
			coldEndInspection: coldEndInspectionPercent,
			coldEndDelta: coldEndDelta
		};
	}

	getLehrSummary(countSummaries: apsViewModels.CountSummary[]): apsViewModels.CountSummary {

		// find lehr summary
		let lehrItem = _.findLast(countSummaries, (c) => {
			return c.name === 'LEHR';
		});

		// add default if nothing is returned
		if (!lehrItem) {
			lehrItem = {
				name: 'LEHR',
				inspectCount: 0.0,
				rejectCount: 0.0,
				rejectPercent: 0.0,
				equipmentCounts: [],
			};
		}

		return lehrItem;
	}

	getHotEndSummary(countSummaries: apsViewModels.CountSummary[]): apsViewModels.CountSummary {

		// find HE summary
		let heItem = _.findLast(countSummaries, (c) => {
			return c.name === 'HE' || c.name === 'CUTS';
		});

		// add default if nothing is returned
		if (!heItem) {
			heItem = {
				name: 'HE',
				inspectCount: 0.0,
				rejectCount: 0.0,
				rejectPercent: 0.0,
				equipmentCounts: [],
			};
		}

		return heItem;
	}

	getColdEndInspectionLoss(countSummaries: apsViewModels.CountSummary[]): number {
		let inspectionLoss = 0.0;
		const inspectionEquipmentItems = this.getColdEndInspectionItems(countSummaries);

		if (inspectionEquipmentItems && inspectionEquipmentItems.length > 0) {
			_.forEach(inspectionEquipmentItems, (cs) => {
				if (cs.rejectCount) {
					inspectionLoss += cs.rejectCount;
				}
			});
		}

		return inspectionLoss;
	}

	getColdEndDeltaLoss(countSummaries: apsViewModels.CountSummary[]): number {
		let coldEndDeltaCount = 0.0;
		let inspectionLoss = 0.0;
		let coldEndOut = 0.0;
		let coldEndInspected = 0.0;
		let coldEndInspectionEquipmentItems = this.getColdEndInspectionItems(countSummaries);

		if (coldEndInspectionEquipmentItems && coldEndInspectionEquipmentItems.length > 0) {
			coldEndInspected = coldEndInspectionEquipmentItems[0].inspectCount;
			let countLen = countSummaries.length;
			let lastItem = countSummaries[countLen - 1];
			coldEndOut = (lastItem.rejectCount && lastItem.rejectCount > 0.0)
				? lastItem.inspectCount - lastItem.rejectCount
				: lastItem.inspectCount;
		}

		if (coldEndInspectionEquipmentItems && coldEndInspectionEquipmentItems.length > 0) {
			_.forEach(coldEndInspectionEquipmentItems, (cs) => {
				inspectionLoss += cs.rejectCount;
			});
		}

		coldEndDeltaCount = (coldEndInspected - coldEndOut) - inspectionLoss;

		return (coldEndDeltaCount / coldEndInspected) * 100;
	}

	getColdEndInspectionItems(countSummaries: apsViewModels.CountSummary[]): apsViewModels.CountSummary[] {
		const coldEndInspectionItems = _.filter(countSummaries, (cs) => {
			return cs.name !== "HE" && cs.name !== "CUTS" && cs.name !== "LEHR";
		});
		return coldEndInspectionItems;
	}

	getHotEndItem(countSummaries: apsViewModels.CountSummary[]): apsViewModels.CountSummary {
		const hotEndItem = _.findLast(countSummaries, (cs) => {
			return cs.name === "HE" || cs.name === "CUTS";
		});
		return hotEndItem;
	}

	getLehrItem(countSummaries: apsViewModels.CountSummary[]): apsViewModels.CountSummary {
		const lehrItem = _.findLast(countSummaries, (cs) => {
			return cs.name === "LEHR" || cs.name === "L-L";
		});
		return lehrItem;
	}

	getCavityCorrelations(cavitiesFromRequest: apsViewModels.CavityCorrelationCountSummary[], cavityLocation: string) {
		const cavityCorrelation: CavityCorrelation[] = [];
		_.forEach(cavitiesFromRequest, (cavityInfo) => {
			if (cavityInfo.cavity.indexOf(cavityLocation) > -1 && cavityInfo.cavity !== "M+U") {
				const cavityName = cavityInfo.cavity;
				const color = this.getCorrelationColor(cavityInfo.inspectCount, cavityInfo.rejectCount);
				const cavityItem: CavityCorrelation = {
					cavityName: cavityName,
					color: color
				};
				cavityCorrelation.push(cavityItem);
			}
		});
		return cavityCorrelation;
	}

	getCorrelationColor(inspectCount: number, rejectCount: number) {
		let color = '000000';
		const effeciencyPercent = 100 * (rejectCount / inspectCount) || 0.0;
		if (effeciencyPercent || effeciencyPercent === 0.0) {
			color = this.uiUtilityService.getCavityCorrelationColor(effeciencyPercent) + 'A0';
		}
		return color;
	}

	getEquipmentCounts(countSummaries: apsViewModels.CountSummary[]) {
		const equipmentCounts: EquipmentCounts[] = [];
		_.forEach(countSummaries, (equipCount) => {
			if (equipCount.name !== "HE" && equipCount.name !== "CUTS"
				&& equipCount.name !== "LEHR" && equipCount.rejectPercent) {
				const eqCount: EquipmentCounts = {
					name: equipCount.name,
					percentLoss: equipCount.rejectPercent
				};
				equipmentCounts.push(eqCount);
			}
		});
		return equipmentCounts;
	}

	countsIntervalChanged(): void {
		this.mapCountInterval();
		const dataPromise = this.plantWareLossService
			.getPlantWareLoss(this.plantId, this.startDate, this.endDate)
			.then((plantWareLossType) => {
				this.mapPlantWareLossType(plantWareLossType);
			});
		this.loaderService.loadingPromise(dataPromise);
	}
}

export default angular.module('aps.controller.plantWareLossDashboard', requires)
	.config(function ($stateProvider: angular.ui.IStateProvider) {
		$stateProvider.state({
			name: States.PLANT_WARE_LOSS_DASHBOARD,
			url: '/plantWareLossDashboard',
			controller: 'PlantWareLossDashboardCtrl as vm',
			template: template,
			data: {
				pageTitle: 'Plant Ware Loss Dashboard'
			}
		});
	})
	.controller('PlantWareLossDashboardCtrl', PlantWareLossDashboardController)
	.name;



// WEBPACK FOOTER //
// ./src/app/controllers/dashboard/plantWareLossDashboard.controller.ts